<script>
  import { onMount, onDestroy } from 'svelte';
  import { readable, get } from 'svelte/store';
  import VectorSource from 'ol/source/Vector.js';
  import GeoJSON from 'ol/format/GeoJSON.js';
  import { transformExtent } from 'ol/proj';

  import AppShell from '../components/AppShell.svelte';
  import {
    apiBaseUrl,
    osmRelationId,
    regionChatUrl,
  } from '../lib/config.js';
  import {
    fetchStandaloneEquipment,
    fetchMeta,
  } from '../lib/api.js';
  import { dataCoverage } from '../stores/dataCoverage.js';
  import { fetchRegionInfo } from '../lib/region.js';
  import { resolveRegionFromPath, isRegionPath } from '../lib/regionUrl.js';
  import { regionFramingApplied } from '../stores/urlFraming.js';
  import { parseHash } from '../lib/deeplink.js';
  import { attachTieredOrchestrator } from '../lib/tieredOrchestrator.js';
  import { regionFitPadding } from '../lib/playgroundHelpers.js';
  import { attachPitchLayer } from '../lib/pitchLayer.js';
  import { mapStore } from '../stores/map.js';
  import { effectiveFilterStore } from '../stores/filters.js';
  import { activeTierStore } from '../stores/tier.js';
  import { selection } from '../stores/selection.js';

  // Standalone owns two VectorSources — one per tier. The orchestrator
  // populates the active one on every debounced moveend; Map.svelte toggles
  // layer visibility from activeTierStore.
  const playgroundSource = new VectorSource(); // polygon tier (zoom > clusterMaxZoom)
  const clusterSource    = new VectorSource(); // cluster tier (zoom ≤ clusterMaxZoom)
  let detachOrchestrator = null;
  let clusterFilterFingerprint = '';

  // Deselect the playground when the user zooms out past the cluster threshold.
  let prevTier = null;
  const detachTierDeselect = activeTierStore.subscribe(tier => {
    if (prevTier === 'polygon' && tier === 'cluster') selection.clear();
    prevTier = tier;
  });

  // Standalone pitches (leisure=pitch outside any playground) — attached to
  // the map when it becomes available (lib/pitchLayer.js).
  let detachPitchLayer = null;
  let detachMapSub = null;
  let regionFitTimer = null;
  let detachRegionFitWatcher = null;

  // Readable store of the current map view in WGS84 for Nominatim `viewbox`.
  // Subscribes to the map's `moveend` when the map becomes available and
  // re-emits the bounds each time the user pans or zooms.
  const searchExtent = readable(null, (set) => {
    let detach = null;
    const unsub = mapStore.subscribe((map) => {
      if (detach) { detach(); detach = null; }
      if (!map) return;
      const update = () => {
        const size = map.getSize();
        if (!size) return;
        set(transformExtent(map.getView().calculateExtent(size), 'EPSG:3857', 'EPSG:4326'));
      };
      map.on('moveend', update);
      detach = () => map.un('moveend', update);
      update();
    });
    return () => {
      if (detach) detach();
      unsub();
    };
  });

  const dataContribLinks = {
    chatUrl: regionChatUrl,
  };

  onMount(async () => {
    // Where this instance has data (#1000): its backend's own bbox, not the
    // Nominatim region, because that is what the import was clipped to.
    if (apiBaseUrl) {
      fetchMeta(apiBaseUrl)
        .then(meta => {
          const bbox = meta?.bbox;
          if (Array.isArray(bbox) && bbox.length === 4 && bbox.every(Number.isFinite)) {
            dataCoverage.set([{ url: apiBaseUrl, bbox }]);
          }
        })
        .catch(() => { /* unknown coverage: the report flow fails open */ });
    }
    // Fit to the configured region and attach the pitch layer once the map
    // becomes available (Map.svelte publishes itself via mapStore on mount).
    detachMapSub = mapStore.subscribe(async (map) => {
      if (!map) return;
      detachMapSub?.();
      detachMapSub = null;

      // Test hook: expose the OL map so the E2E suite can assert where the
      // view came to rest (e.g. a deeplink frames the linked playground
      // rather than the region). HubApp publishes the same property; without
      // it here, the standalone deeplink test could only assert that a panel
      // opened, which it does whether or not the map was moved away
      // afterwards — which is how #774 stayed invisible.
      // Namespaced under `__spieli` so it does not collide with anything else.
      if (typeof window !== 'undefined') {
        window.__spieli = window.__spieli ?? {};
        window.__spieli.map = map;
      }

      // Region fit via Nominatim bbox. The deeplink hash (if any) is read
      // BEFORE the await so the decision can't be invalidated by anything
      // that mutates window.location.hash while we're waiting on Nominatim.
      const hadDeeplink = parseHash(window.location.hash);
      const regionPath = isRegionPath(window.location.pathname);
      try {
        // Fetch the configured region first so its centre can disambiguate
        // same-named places in a region URL (e.g. /Lauterbach → Hessen, not
        // the higher-importance Lauterbach in Czechia). Also serves as the
        // fallback framing when there is no region override.
        const regionInfo = await fetchRegionInfo(osmRelationId).catch(() => null);
        const regionOverride = await resolveRegionFromPath(window.location.pathname, {
          near: regionInfo?.center,
        });
        // Tell LocateButton whether an explicit region URL actually framed the
        // map: true when the override resolved, false when a region path was
        // present but fell back to the configured region.
        if (regionPath) regionFramingApplied.set(!!regionOverride);
        const region = regionOverride || regionInfo;
        if (!region) throw new Error('no region info available');
        document.title = `spieli ${region.name}`;
        const regionExtent = transformExtent(region.extent, 'EPSG:4326', 'EPSG:3857');
        const fitToRegion = () => {
          map.getView().fit(regionExtent, {
            padding: regionFitPadding(),
            duration: 0,
          });
        };
        if (hadDeeplink) {
          // Deeplink hash present — the deeplink expresses explicit user
          // intent that takes precedence over the default region framing,
          // so we don't fit eagerly. But if AppShell.tryRestoreFromHash
          // can't deliver (osm_id 404, hydration error, unknown slug, etc.)
          // no fit ever happens and the user lands on the OL default view.
          // Fall back to the region after a short delay if nothing was
          // restored.
          //
          // The success signal is the `selection` store, not a `moveend`
          // (#774). Two separate defects came from using the event:
          //
          //  1. A map event is edge-triggered: it exists only at the instant
          //     it fires. This watcher was armed AFTER awaiting Nominatim, so
          //     a slow lookup meant the restore's moveend had already passed
          //     with nothing listening — the fallback then fired and threw
          //     away the framing the restore had just done. Arming earlier
          //     would only have shrunk that window. Subscribing to a store
          //     removes it: a store replays its current value on subscribe,
          //     so a restore that already happened is still observed.
          //
          //  2. `moveend` means "the map moved", not "the deeplink restored".
          //     Any movement during load — a pan, auto-locate centring on a
          //     GPS fix — satisfied it, suppressing a fallback that should
          //     have run and stranding the visitor on the default extent.
          //
          // `restored` latches, and that is load-bearing rather than
          // incidental: the question is "did a restore ever deliver", not
          // "is something selected right now". Two paths clear the selection
          // well inside 1500 ms — the tier deselect above (zoom out past
          // clusterMaxZoom) and AppShell's mobile "back to map" — and reading
          // the store live at timer time treats either as "nothing was
          // restored", yanking the view to the region on top of a deliberate
          // user action. The old moveend flag latched too, which is why it
          // never showed this.
          let restored = false;
          detachRegionFitWatcher = selection.subscribe(sel => {
            if (sel.feature) restored = true;
          });
          regionFitTimer = setTimeout(() => {
            regionFitTimer = null;
            detachRegionFitWatcher?.();
            detachRegionFitWatcher = null;
            if (!restored) fitToRegion();
          }, 1500);
        } else {
          // No deeplink: frame the resolved region, or fall back to the
          // configured region when a region path didn't resolve (e.g. a typo).
          // In the fallback case framingApplied is false, so auto-locate still
          // centers on the GPS fix when available — GPS wins for a typo, but a
          // user without geolocation sees the configured region rather than the
          // bare default extent (#698 review).
          fitToRegion();
        }
      } catch (err) {
        // Region framing failed entirely — a region path, if present, did not
        // take effect, so let auto-locate center on the GPS fix.
        if (regionPath) regionFramingApplied.set(false);
        console.warn('Nominatim region fetch failed, using default extent:', err);
      }

      // Standalone pitch layer — shared with the hub (lib/pitchLayer.js);
      // here it loads from this instance's own backend.
      detachPitchLayer = attachPitchLayer({
        map,
        load: async (extent, signal) => new GeoJSON().readFeatures(
          await fetchStandaloneEquipment(extent, apiBaseUrl, signal),
          { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' },
        ),
      });

      // Tiered orchestrator: replaces the one-shot region fetch. Picks a
      // tier from the view's zoom on every moveend, fetches the matching
      // RPC, and populates the relevant source. Cancels superseded requests.
      //
      // When apiBaseUrl is empty (local-dev without Docker, see CLAUDE.md
      // "Local dev note") there is no data path — skip the orchestrator so
      // the console isn't spammed with 404s against the Vite dev server.
      if (apiBaseUrl) {
        const orchestrator = attachTieredOrchestrator({
          map,
          baseUrl: apiBaseUrl,
          clusterSource,
          polygonSource: playgroundSource,
          getFilters: () => clusterFilterFingerprint ? JSON.parse(clusterFilterFingerprint) : null,
        });
        detachOrchestrator = orchestrator.detach;

        // Subscribe to filter changes imperatively — avoids Svelte $: dependency-tracking
        // uncertainty for variables set inside async/nested callbacks.
        // Skip the immediate invocation (filters start all-false, orchestrator just ran).
        let filterSubReady = false;
        // effectiveFilterStore, not filterStore: it also emits when mapping
        // detail is toggled, and never carries hidden completeness states
        // while it is off (#924).
        const unsubFilters = effectiveFilterStore.subscribe(filters => {
          const { standalonePitches: _sp, ...cf } = filters;
          const fingerprint = JSON.stringify(cf);
          // Only refetch when the filters that reach the server changed: toggling
          // mapping detail with nothing hidden, or the pitch layer, emits here too.
          const changed = fingerprint !== clusterFilterFingerprint;
          clusterFilterFingerprint = fingerprint;
          if (!filterSubReady) { filterSubReady = true; return; }
          if (!changed) return;
          if (get(activeTierStore) === 'cluster') orchestrator.rerun();
        });
        const baseDetach = orchestrator.detach;
        detachOrchestrator = () => { baseDetach(); unsubFilters(); };
      } else {
        console.info('[standalone] apiBaseUrl empty — tiered orchestrator not attached (local dev without Docker)');
      }
    });
  });

  onDestroy(() => {
    detachTierDeselect();
    if (detachMapSub)      detachMapSub();
    if (detachPitchLayer)  detachPitchLayer();
    if (detachOrchestrator) detachOrchestrator();
    if (regionFitTimer)    clearTimeout(regionFitTimer);
    if (detachRegionFitWatcher) detachRegionFitWatcher();
  });
</script>

<AppShell
  {playgroundSource}
  {clusterSource}
  {searchExtent}
  {dataContribLinks}
  defaultBackendUrl={apiBaseUrl}
/>
